import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Drawer, 
  IconButton, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Typography, 
  useTheme, 
  styled 
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  ChevronLeft as ChevronLeftIcon, 
  AccessTime as ClockIcon, 
  CreditCard as PaymentsIcon, 
  People as ClientsIcon, 
  History as HistoryIcon, 
  Person as AccountIcon, 
  ChevronRight as ChevronRightIcon 
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Styled components
const CircleButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: '16px',
  left: '16px',
  zIndex: 1300,
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[3],
  transition: 'all 0.3s ease',
  width: 40,
  height: 40,
  borderRadius: '50%',
  '&:hover': {
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.common.white,
  },
}));

const MenuList = styled(List)(({ theme }) => ({
  paddingTop: 0,
  paddingBottom: 0,
}));

const MenuItem = styled(ListItem)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  borderRadius: '8px',
  margin: '4px 0',
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  backgroundColor: active ? theme.palette.grey[50] : 'transparent',
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: active ? 500 : 400,
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}));

// Application items data


const Sidebar = () => {


  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const appItems = [
    {
      id: 'scheduler',
      name: t('therapistdashboardv2.menu.scheduler'),
      icon: <ClockIcon />,
      path: '/dashboard',
    },
    {
      id: 'payments',
      name: t('therapistdashboardv2.menu.payments'),
      icon: <PaymentsIcon />,
      path: '/dashboard/payments',
    },
    {
      id: 'clients',
      name: t('therapistdashboardv2.menu.clients'),
      icon: <ClientsIcon />,
      path: '/dashboard/clients',
    },
    {
      id: 'history',
      name: t('therapistdashboardv2.menu.history'),
      icon: <HistoryIcon />,
      path: '/dashboard/history',
    },
    {
      id: 'account',
      name: t('therapistdashboardv2.menu.account'),
      icon: <AccountIcon />,
      path: '/dashboard/account',
    },
    {
      id: 'Forms',
      name: t('therapistdashboardv2.menu.forms'),
      icon: <AccountIcon />,
      path: '/dashboard/forms',
    },
  ];
  
  // Find active item based on current path
  const findActiveItem = () => {
    const currentPath = location.pathname;
    const activeItem = appItems.find(item => 
      currentPath === item.path || currentPath.startsWith(item.path)
    );
    return activeItem ? activeItem.id : 'scheduler'; // Default to scheduler if no match
  };
  
  const [activeItem, setActiveItem] = useState(findActiveItem);
  
  // Update active item when location changes
  useEffect(() => {
    setActiveItem(findActiveItem());
  }, [location.pathname]);

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleItemClick = (id, path) => {
    setActiveItem(id);
    navigate(path);
    
    // Optional: close sidebar on mobile after selection
    if (window.innerWidth < 600) setOpen(false);
  };

  return (
    <>
      {/* Toggle Button */}
      <CircleButton 
        onClick={toggleDrawer} 
        color="primary" 
        aria-label="toggle menu"
      >
        {open ? <ChevronLeftIcon /> : <MenuIcon />}
      </CircleButton>

      {/* Sidebar Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        onClose={toggleDrawer}
        sx={{
          width: 240,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            border: 'none',
            boxShadow: theme.shadows[5],
            paddingTop: '64px',
          },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Box sx={{ px: 3, mb: 3 }}>
          <Typography variant="h5" fontWeight={600}>
            Applications
          </Typography>
        </Box>

        <MenuList>
          {appItems.map((item) => (
            <MenuItem 
              key={item.id} 
              active={activeItem === item.id ? 1 : 0}
              onClick={() => handleItemClick(item.id, item.path)}
            >
              <ListItemIcon sx={{ 
                color: activeItem === item.id ? theme.palette.primary.main : 'inherit',
                minWidth: 36,
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.name} />
              {activeItem === item.id && (
                <ChevronRightIcon fontSize="small" sx={{ ml: 'auto' }} />
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Drawer>
    </>
  );
};

export default Sidebar;